import React, {useState} from 'react';
import {useServiceProviderControl} from '../hooks/useServiceProviderControl';
import {ServiceProvider} from '../models/serviceProvider.model';
import {FormField, FormSection, FormActions} from '../../../Presentation/components/common';
import {useFormState, CommonValidationRules, createFieldProps} from '../../../Presentation/utils/formUtils';


interface AddServiceProviderFormProps {
  onSuccess?: (provider: ServiceProvider) => void;
  onCancel?: () => void;
}

export default function AddServiceProviderForm ({onSuccess, onCancel}: AddServiceProviderFormProps) {
  const {createProvider} = useServiceProviderControl();

  // Local state for password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Centralized form state
  const [formState, formActions] = useFormState(
    {
      username: '',
      fullName: '',
      email: '',
      phoneNumber: '',
      password: '',
      companyName: '',
      website: '',
      description: ''
    },
    {
      username: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'اسم المستخدم مطلوب';
          }
          if (value.length < 3) {
            return 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل';
          }
          if (!/^[a-zA-Z0-9_]+$/.test(value)) {
            return 'اسم المستخدم يجب أن يحتوي على أحرف وأرقام و _ فقط';
          }
          return null;
        }
      },
      fullName: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'الاسم الكامل مطلوب';
          }
          if (value.length < 2) {
            return 'الاسم الكامل يجب أن يكون حرفين على الأقل';
          }
          return null;
        }
      },
      email: CommonValidationRules.email,
      phoneNumber: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'رقم الهاتف مطلوب';
          }
          const phoneRegex = /^(\+966|966|0)?5[0-9]{8}$/;
          if (!phoneRegex.test(value.replace(/\s+/g, ''))) {
            return 'صيغة رقم الهاتف غير صحيحة (يجب أن يكون رقم سعودي)';
          }
          return null;
        }
      },
      password: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'كلمة المرور مطلوبة';
          }
          if (value.length < 8) {
            return 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
          }
          if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
            return 'كلمة المرور يجب أن تحتوي على حرف صغير وكبير ورقم';
          }
          return null;
        }
      }
      // companyName is optional, no validation needed
    }
  );

  const handleSubmit = async (data: any) => {
    try {
      // Clean and prepare data in camelCase (as ServiceProvider model expects)
      // The Repository layer will convert to snake_case for the API
      const providerData = {
        username: data.username?.toLowerCase().trim() || '',
        fullName: data.fullName?.trim() || '',
        email: data.email?.toLowerCase().trim() || '',
        phoneNumber: data.phoneNumber?.replace(/\s+/g, '') || '',
        password: data.password || '',
        companyName: data.companyName?.trim() || '',
        website: data.website?.trim() || '',
        description: data.description?.trim() || ''
      };

      // Send to store/useCase/repository (they expect camelCase)
      const newProvider = await createProvider(providerData);

      onSuccess?.(newProvider);
    } catch (error) {
      // Error notification is handled by StandardizedErrorHandler in the store
    }
  };

  const generateUsername = () => {
    // Generate simple username with random number
    const randomNum = Math.floor(Math.random() * 9999) + 1000; // 4-digit number
    const username = `provider_${randomNum}`;

    formActions.setField('username', username);
  };

  const generatePassword = () => {
    // Generate simple but strong password
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';

    // Start with a capital letter
    password += 'P'; // For Provider

    // Add random characters
    for (let i = 1; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Add a number at the end
    password += Math.floor(Math.random() * 99) + 10;

    formActions.setField('password', password);
  };

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); formActions.submit(handleSubmit); }} className="space-y-4">
          <FormSection title="بيانات مزود الخدمة">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                اسم المستخدم *
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <FormField
                    type="text"
                    placeholder="provider_company_123"
                    {...createFieldProps('username', formState, formActions)}
                  />
                </div>
                <button
                  type="button"
                  onClick={generateUsername}
                  className="px-3 py-2 text-sm bg-brand-500 text-white rounded-md hover:bg-brand-600"
                  title="توليد اسم مستخدم عشوائي"
                >
                  توليد
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                يُستخدم لتسجيل الدخول. انقر "توليد" لإنشاء اسم مستخدم تلقائياً
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                type="text"
                label="الاسم الكامل *"
                placeholder="أحمد محمد مزود الخدمة"
                {...createFieldProps('fullName', formState, formActions)}
              />
              <FormField
                type="email"
                label="البريد الإلكتروني *"
                placeholder="ahmed.provider@company.com"
                {...createFieldProps('email', formState, formActions)}
              />
              <FormField
                type="tel"
                label="رقم الهاتف *"
                placeholder="+966501234567"
                {...createFieldProps('phoneNumber', formState, formActions)}
              />
              <FormField
                type="text"
                label="اسم الشركة"
                placeholder="شركة الخدمات المحدودة (اختياري)"
                {...createFieldProps('companyName', formState, formActions)}
              />
            </div>

            <FormField
              type="url"
              label="الموقع الإلكتروني"
              placeholder="https://www.company.com"
              {...createFieldProps('website', formState, formActions)}
            />

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                كلمة المرور *
              </label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <FormField
                    type={showPassword ? 'text' : 'password'}
                    placeholder="SecurePassword123"
                    {...createFieldProps('password', formState, formActions)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={generatePassword}
                  className="px-3 py-2 text-sm bg-green-500 text-white rounded-md hover:bg-green-600"
                  title="توليد كلمة مرور قوية"
                >
                 توليد
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                8 أحرف على الأقل. انقر "توليد" لإنشاء كلمة مرور قوية تلقائياً
              </p>
            </div>

            <FormField
              type="textarea"
              label="الوصف"
              placeholder="وصف مختصر عن مزود الخدمة والخدمات المقدمة"
              rows={3}
              {...createFieldProps('description', formState, formActions)}
            />
          </FormSection>

          <FormActions
            onSubmit={() => formActions.submit(handleSubmit)}
            onCancel={onCancel}
            submitLabel="إضافة مزود الخدمة"
            cancelLabel="إلغاء"
            isLoading={formState.isSubmitting}
            disabled={!formState.isValid}
            showReset={false}
          />
      </form>

      {/* Guidelines */}
      <div className="mt-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
        <h4 className="text-sm font-semibold text-brand-900 mb-2">إرشادات مهمة:</h4>
        <ul className="text-xs text-brand-800 space-y-1">
          <li>• تأكد من صحة البريد الإلكتروني ورقم الهاتف</li>
          <li>• اسم المستخدم يجب أن يكون فريداً في النظام</li>
          <li>• كلمة المرور يجب أن تكون قوية وآمنة</li>
          <li>• سيتم تعيين دور "مزود خدمة" تلقائياً للمستخدم الجديد</li>
        </ul>
      </div>
    </div>
  );
}
